import { useEffect } from 'react';
import i18n from '../../i18n';
import { exposeSeoRoutes } from '../../content/seoRoutes';

declare global {
	interface Window {
		__PRERENDER_READY__?: boolean;
	}
}

/**
 * Signals to the build-time prerenderer (scripts/prerender.js) that the page is
 * safe to snapshot, and publishes the SEO route manifest on `window` so the
 * same script can discover which routes to snapshot.
 *
 * The readiness wait exists because i18next loads its translation bundles over
 * HTTP from /locales at runtime. Snapshotting too early bakes raw translation
 * keys ("Page {{n}} of {{total}}") into the static HTML — which then ships to
 * crawlers. So we wait for i18next to report `initialized` *and* for the
 * translation bundle of the active language to be present, then yield two
 * animation frames so react-helmet-async has flushed its tags into <head>.
 *
 * No-op in normal browser usage beyond setting one boolean and one array.
 */
const PrerenderReady = () => {
	useEffect(() => {
		exposeSeoRoutes();

		let cancelled = false;

		const markReady = () => {
			if (cancelled) return;
			// Two frames: one for React to commit, one for Helmet's head flush.
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (!cancelled) window.__PRERENDER_READY__ = true;
				});
			});
		};

		const hasBundle = () =>
			!!i18n.language && i18n.hasResourceBundle(i18n.language, 'translation');

		if (i18n.isInitialized && hasBundle()) {
			markReady();
		} else {
			const onLoaded = () => {
				if (hasBundle()) markReady();
			};
			i18n.on('initialized', onLoaded);
			i18n.on('loaded', onLoaded);
			// Fallback: never block the snapshot indefinitely if a bundle 404s.
			const timer = setTimeout(markReady, 8000);
			return () => {
				cancelled = true;
				clearTimeout(timer);
				i18n.off('initialized', onLoaded);
				i18n.off('loaded', onLoaded);
			};
		}

		return () => {
			cancelled = true;
		};
	}, []);

	return null;
};

export default PrerenderReady;
